import { Cookie, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCookieConsent } from '../hooks/useCookieConsent';

const COOKIE_CATEGORIES = [
  { label: 'Strictly Necessary', desc: 'Required for the site to function. Cannot be disabled.', locked: true },
  { label: 'Analytics', desc: 'Helps us understand how visitors interact with the site.', locked: false },
  { label: 'Marketing', desc: 'Used to deliver relevant ads and track campaign performance.', locked: false },
];

function PreferencesPanel({ onCollapse }) {
  return (
    <div style={{
      marginTop: '14px', padding: '14px',
      backgroundColor: 'rgba(255,255,255,0.04)',
      borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)',
    }}>
      {COOKIE_CATEGORIES.map(({ label, desc, locked }, idx) => (
        <div key={label} style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          gap: '12px', padding: '10px 0',
          borderBottom: idx < COOKIE_CATEGORIES.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
        }}>
          <div>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.8rem', fontWeight: 700, color: '#FFFFFF', marginBottom: '2px' }}>
              {label}
              {locked && (
                <span style={{ fontSize: '0.65rem', color: '#64748B', fontWeight: 400, marginLeft: '8px', letterSpacing: '0.05em' }}>
                  Always on
                </span>
              )}
            </p>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.74rem', color: 'rgba(255,255,255,0.45)' }}>
              {desc}
            </p>
          </div>
          <div style={{
            width: '36px', height: '20px', borderRadius: '100px',
            backgroundColor: locked ? '#1B3D7E' : 'rgba(255,255,255,0.15)',
            flexShrink: 0, marginTop: '2px',
            cursor: locked ? 'default' : 'pointer', position: 'relative',
          }}>
            <div style={{
              width: '14px', height: '14px', borderRadius: '50%',
              backgroundColor: '#FFFFFF', position: 'absolute',
              top: '3px', left: locked ? '19px' : '3px', transition: 'left 0.2s',
            }} />
          </div>
        </div>
      ))}
      <button
        onClick={onCollapse}
        style={{
          marginTop: '10px', background: 'none', border: 'none',
          color: '#93B4FF', cursor: 'pointer', fontFamily: 'Manrope, sans-serif',
          fontSize: '0.78rem', padding: 0, display: 'flex', alignItems: 'center', gap: '3px',
        }}
      >
        <ChevronUp size={12} /> Collapse
      </button>
    </div>
  );
}

export default function CookieConsentBanner() {
  const { visible, expanded, setExpanded, accept, decline } = useCookieConsent();

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        zIndex: 9998, padding: '0 24px 24px', pointerEvents: 'none',
      }}
    >
      <div style={{
        maxWidth: '900px', margin: '0 auto',
        backgroundColor: '#0D1A2E', borderRadius: '16px',
        boxShadow: '0 -4px 40px rgba(13,26,46,0.25)',
        overflow: 'hidden', pointerEvents: 'all',
        border: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', padding: '20px 24px', flexWrap: 'wrap' }}>
          {/* Icon */}
          <div style={{
            width: '38px', height: '38px', borderRadius: '10px',
            backgroundColor: 'rgba(27,61,126,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, marginTop: '2px',
          }}>
            <Cookie size={18} style={{ color: '#93B4FF' }} strokeWidth={1.5} />
          </div>

          {/* Text + preferences */}
          <div style={{ flex: 1, minWidth: '200px' }}>
            <p style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: '0.95rem', color: '#FFFFFF', marginBottom: '4px' }}>
              We use cookies
            </p>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.8rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.65 }}>
              We use cookies to personalise content, analyse site traffic, and serve relevant marketing. By clicking &ldquo;Accept All&rdquo; you consent to our use of cookies.{' '}
              <Link to="/privacy" style={{ color: '#93B4FF', textDecoration: 'underline' }}>Privacy Policy</Link>
              {!expanded && (
                <button
                  data-testid="cookie-banner-expand"
                  onClick={() => setExpanded(true)}
                  style={{
                    background: 'none', border: 'none', color: '#93B4FF', cursor: 'pointer',
                    fontFamily: 'Manrope, sans-serif', fontSize: '0.8rem',
                    padding: '0 0 0 6px', display: 'inline-flex', alignItems: 'center', gap: '3px',
                  }}
                >
                  Manage preferences <ChevronDown size={12} />
                </button>
              )}
            </p>
            {expanded && <PreferencesPanel onCollapse={() => setExpanded(false)} />}
          </div>

          {/* CTAs */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
            <button
              data-testid="cookie-banner-decline"
              onClick={decline}
              style={{
                background: 'none', border: 'none', color: 'rgba(255,255,255,0.45)',
                fontFamily: 'Manrope, sans-serif', fontSize: '0.8rem', fontWeight: 600,
                cursor: 'pointer', padding: '4px 8px', transition: 'color 0.2s', whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.75)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.45)')}
            >
              Decline
            </button>
            <button
              data-testid="cookie-banner-accept"
              onClick={accept}
              style={{
                backgroundColor: '#1B3D7E', color: '#FFFFFF', border: 'none',
                borderRadius: '10px', padding: '10px 22px',
                fontFamily: 'Manrope, sans-serif', fontSize: '0.82rem', fontWeight: 700,
                letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer',
                transition: 'background-color 0.2s', whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#152e61')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#1B3D7E')}
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
